/**
 *
 * GSTR-9C New Save
 *
 */

import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RingLoader } from "react-spinners";
import NavBar from "components/SideBar/SideBar2";
import { Button } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import Filter from "../Filter";
import TableHeader from "../TableHeader";
import SaveTab from "./SaveTab";
import FetchedSummary from "./FetchedSummary";
import {
  makeSelectSelectedEntity,
  makeSelectSelectedGstin,
  makeSelectSelectedReturnPeriod,
} from "../common/selectors";
import { COMMON_MSGS } from "Constant/Messages";
import { errorToastMessage } from "utils/errorToast";
import {
  setSelectedSave,
  setSelectedRowSave,
  getSaveSubmitData,
  getFetchSubmitData,
  saveToGstn,
  downloadJson,
  setSaveTableData,
  setFetchTableData,
  clearAllSelectedStates,
} from "./actions";
import { setSelectedEntity, setSelectedGstin, setSelectedReturnPeriod } from "../common/actions";

const headerColumns = [
  {
    value: "Save",
    id: "1",
  },
  {
    value: "Fetched Summary",
    id: "2",
  },
];

const Save = (props) => {
  const interval = useRef(null);
  const dispatch = useDispatch();

  // Redux selectors
  const {
    loaderStatus,
    saveTableData,
    fetchTableData,
    selectedSave,
    selectedRowSave,
  } = useSelector((state) => state.gstr9CNewSaveReducer);
  const selectedEntity = useSelector(makeSelectSelectedEntity());
  const selectedGstinList = useSelector(makeSelectSelectedGstin());
  const selectedReturnPeriod = useSelector(makeSelectSelectedReturnPeriod());

  const [activeTab, setActiveTab] = useState("1");

  // Validation function to check if all required fields are selected
  const validateRequiredFields = () => {
    return (
      !selectedEntity ||
      selectedGstinList?.length === 0 ||
      !selectedReturnPeriod
    );
  };

  // Handle tab change function
  const handleTabChange = (tabId) => {
    console.log("Tab changed to:", tabId);
    setActiveTab(tabId);
  };

  // Helper function to count in-process files
  const countInProcessFiles = (files) =>
    files?.filter((item) => {
      const status = item?.STATUS?.toUpperCase() || item?.status?.toUpperCase();
      // Consider null, undefined, or any status that's not COMPLETED/SUCCESS/FAILED as in-progress
      return !status || !["COMPLETED", "SUCCESS", "FAILED"].includes(status);
    })?.length;

  // Function to clear interval and restart data fetching (for fetch button)
  const clearAndRestartInterval = (forceClear = false) => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
      console.log(
        "🧹 Interval cleared",
        forceClear ? "(forced by fetch button)" : "(normal clear)"
      );
    }
  };

  // Fetch data function
  const getSaveData = async (isToast = true) => {
    console.log("🚀 getSaveData called");

    // Clear existing interval before starting new data fetch
    clearAndRestartInterval();

    try {
      console.log("📡 Starting initial data fetch...");
      let fileDataTemp = await dispatch(
        getSaveSubmitData(selectedGstinList, selectedReturnPeriod, isToast)
      );

      console.log("✅ Initial data fetch completed:", {
        totalRecords: fileDataTemp?.length,
        data: fileDataTemp,
      });

      // Count in-process files
      let inProcessStatusCount = countInProcessFiles(fileDataTemp);
      console.log("📊 In-process files count:", inProcessStatusCount);
      
      // Debug: Log individual item statuses to understand the data structure
      console.log("🔍 Debug - File data statuses:", fileDataTemp?.map(item => ({
        FILE_ID: item.FILE_ID || item.file_id,
        STATUS: item.STATUS,
        status: item.status,
        combinedStatus: item?.STATUS || item?.status
      })));

      // Log status distribution for better monitoring
      const statusDistribution = fileDataTemp?.reduce((acc, item) => {
        const status = item?.STATUS || item?.status;
        const statusKey =
          status === null || status === ''
            ? "IN-PROGRESS"
            : status?.toString().toUpperCase() || "UNKNOWN";
        acc[statusKey] = (acc[statusKey] || 0) + 1;
        return acc;
      }, {});
      console.log("📈 Status distribution:", statusDistribution);

      if (fileDataTemp?.length > 0 && inProcessStatusCount > 0) {
        console.log("⏱️ Starting interval polling for in-process files...");
        console.log(
          "🔄 Interval will run every 10 seconds until all files are processed"
        );

        let intervalCount = 0;
        let currentInProcessCount = inProcessStatusCount;

        interval.current = setInterval(async () => {
          intervalCount++;
          console.log(
            `\n🔄 Interval execution #${intervalCount} - ${new Date().toLocaleTimeString()}`
          );

          try {
            console.log("📡 Fetching updated data...");
            const updatedFileData = await dispatch(
              getSaveSubmitData(selectedGstinList, selectedReturnPeriod, false)
            );

            console.log("✅ Updated data fetch completed:", {
              totalRecords: updatedFileData?.length,
              intervalExecution: intervalCount,
            });

            // Count in-process files
            const previousCount = currentInProcessCount;
            currentInProcessCount = countInProcessFiles(updatedFileData);

            console.log("📊 Updated in-process files count:", {
              current: currentInProcessCount,
              previous: previousCount,
              change: previousCount - currentInProcessCount,
            });

            // Clear interval if no files are in process
            if (currentInProcessCount === 0) {
              clearInterval(interval.current);
              interval.current = null;
              console.log(
                "🎉 All files processed! Interval cleared after",
                intervalCount,
                "executions"
              );
            } else {
              console.log(
                `⏳ Still ${currentInProcessCount} files in process. Continuing to poll...`
              );
            }
          } catch (intervalError) {
            console.error("❌ Error during interval execution:", {
              intervalCount,
              error: intervalError,
              message: intervalError.message,
            });
          }
        }, 10000);
      } else if (fileDataTemp?.length === 0) {
        console.log("ℹ️ No data returned from API - skipping interval setup");
      } else {
        console.log("✅ All files already processed - no interval needed");
      }
    } catch (error) {
      console.error("❌ Error in getFileData:", {
        error,
        message: error.message,
        stack: error.stack,
      });
    }
  };

  // Fetch data function for Fetch tab
  const getFetchData = async (isToast = true) => {
    console.log("🚀 getFetchData called");

    // Clear existing interval before starting new data fetch
    clearAndRestartInterval();

    try {
      console.log("📡 Starting fetch data retrieval...");
      let fetchDataTemp = await dispatch(
        getFetchSubmitData(selectedGstinList, selectedReturnPeriod, isToast)
      );

      console.log("✅ Fetch data retrieval completed:", {
        totalRecords: fetchDataTemp?.length,
        data: fetchDataTemp,
      });

      // Count in-process files for fetch data
      let inProcessStatusCount = countInProcessFiles(fetchDataTemp);
      console.log("📊 In-process fetch files count:", inProcessStatusCount);
      
      // Debug: Log individual item statuses to understand the data structure
      console.log("🔍 Debug - Fetch data statuses:", fetchDataTemp?.map(item => ({
        FILE_ID: item.FILE_ID || item.file_id,
        STATUS: item.STATUS,
        status: item.status,
        combinedStatus: item?.STATUS || item?.status
      })));

      // Log status distribution for better monitoring
      const statusDistribution = fetchDataTemp?.reduce((acc, item) => {
        const status = item?.STATUS || item?.status;
        const statusKey =
          status === null
            ? "IN-PROGRESS"
            : status?.toString().toUpperCase() || "UNKNOWN";
        acc[statusKey] = (acc[statusKey] || 0) + 1;
        return acc;
      }, {});
      console.log("📈 Fetch status distribution:", statusDistribution);

      if (fetchDataTemp?.length > 0 && inProcessStatusCount > 0) {
        console.log(
          "⏱️ Fetch data has in-process files - interval management will be handled by FetchedSummary component"
        );
        console.log(
          "ℹ️ Parent component will not start its own interval to avoid conflicts"
        );
      } else if (fetchDataTemp?.length === 0) {
        console.log(
          "ℹ️ No fetch data returned from API - skipping interval setup"
        );
      } else {
        console.log(
          "✅ All fetch files already processed - no interval needed"
        );
      }
    } catch (error) {
      console.error("❌ Error in getFetchData:", {
        error,
        message: error.message,
        stack: error.stack,
      });
    }
  };

  // Handle Submit for Save Tab
  const handleSubmitSave = async() => {
    if (validateRequiredFields()) {
      return errorToastMessage(COMMON_MSGS.ENTER_MANDATORY_FIELDS);
    }
    if (interval.current) {
      clearInterval(interval.current);
    }
    await dispatch(
        getSaveSubmitData(selectedGstinList, selectedReturnPeriod, true)
      );
    // getSaveData(true);
  };

  // Handle Submit for Fetch Tab
  const handleSubmitFetch = async() => {
    if (validateRequiredFields()) {
      return errorToastMessage(COMMON_MSGS.ENTER_MANDATORY_FIELDS);
    }
    if (interval.current) {
      clearInterval(interval.current);
    }
    
    // Clear selected states before fetching new data
    dispatch(clearAllSelectedStates());
    
    // await dispatch(
    //     getFetchSubmitData(selectedGstinList, selectedReturnPeriod, true)
    //   );
    getFetchData(true);
  };

  // Handle Submit based on active tab
  const handleSubmit = () => {
    if (activeTab === "1") {
      handleSubmitSave();
    } else if (activeTab === "2") {
      handleSubmitFetch();
    }
  };

  // Handle Save to GSTN
  const handleSaveToGSTN = async () => {
    if (validateRequiredFields()) {
      return errorToastMessage(COMMON_MSGS.ENTER_MANDATORY_FIELDS);
    }

    if (selectedSave?.length === 0) {
      return errorToastMessage(
        "Please select at least one row to save to GSTN"
      );
    }
    try {
      await dispatch(saveToGstn(selectedRowSave));
    //   getSaveData(false);
    await dispatch(
        getSaveSubmitData(selectedGstinList, selectedReturnPeriod, false)
      );
      dispatch(setSelectedSave([]));
      dispatch(setSelectedRowSave([]));
    } catch (error) {
      console.log("error in handleSaveToGSTN::", error);
    }
  };

  // Handle Download JSON
  const handleDownloadJSON = async () => {
    if(!selectedSave || selectedSave?.length === 0){
        return errorToastMessage("Please select at least one row to download JSON");
    }
    try {
      dispatch(downloadJson(selectedRowSave));
      dispatch(setSelectedSave([]));
      dispatch(setSelectedRowSave([]))
    } catch (error) {
      console.log("Error in handleDownloadJSON::", error);
    }
  };

  // Clear interval when tab changes and restart if returning to tab with data
//   useEffect(() => {
//     console.log("🔄 Tab changed to:", activeTab);

//     // Always clear interval when tab changes
//     if (interval.current) {
//       clearInterval(interval.current);
//       interval.current = null;
//       console.log("🧹 Interval cleared due to tab change");
//     }

//     // If switching back to Save tab and we have data with in-process files, restart interval
//     if (activeTab === "1" && saveTableData?.length > 0) {
//       const inProcessCount = countInProcessFiles(saveTableData);
//       console.log(
//         "📊 Save tab activated - checking for in-process files:",
//         inProcessCount
//       );

//       if (inProcessCount > 0) {
//         console.log("⏱️ Restarting data polling by calling getSaveData...");
//         getSaveData(false); // Call getSaveData without toast notification
//       }
//     }

//     // If switching back to Fetch tab and we have data, refresh it
//     if (activeTab === "2" && fetchTableData?.length > 0) {
//       const inProcessCount = countInProcessFiles(fetchTableData);
//       console.log(
//         "📊 Fetch tab activated - checking for in-process files:",
//         inProcessCount
//       );

//       if (inProcessCount > 0) {
//         console.log("🔄 Refreshing fetch data by calling getFetchData...");
//         getFetchData(false); // Call getFetchData without toast notification
//       }
//     }
//   }, [activeTab]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
        console.log("🧹 Interval cleared on component unmount");
      }

      // Clear selected data using existing actions
    dispatch(setSelectedSave([]));
    dispatch(setSelectedRowSave([]));
    console.log("🧹 Cleared selected save data and selected rows");

    // Clear filter states using existing actions
    dispatch(setSelectedEntity(""));
    dispatch(setSelectedGstin(""));
    dispatch(setSelectedReturnPeriod(""));
    console.log("🧹 Cleared selected entity, GSTIN, and return period");

    // Reset local component state
    dispatch(setSaveTableData([]));
    dispatch(setFetchTableData([]));
    console.log("🧹 Save & fetch table data cleared");

    console.log("🧹 Save Component cleanup completed - all states cleared");
    };
  }, []);

  return (
    <div>
      <Helmet>
        <title>GSTR-9C | Save</title>
        <meta name="description" content="Save GSTR-9C return data" />
      </Helmet>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnVisibilityChange
        draggable
        pauseOnHover
      />
      <div className="sweet-loading">
        <RingLoader size={100} color={"#4f2d7f"} loading={loaderStatus} />
      </div>
      <NavBar
        currentPage="GSTR-9C | Save"
        module="gstr9_new"
        history={props.history}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ width: "70%" }}>
              <Filter entityMasterList={[]} profileData={[]} activeTab={0} />
            </div>
            <div style={{ width: "25%",  marginLeft: "auto" }}>
             <label style={{fontFamily: "GT-Walsheim-medium", fontSize: "12px", color: "#f8f9fa", display: "block", marginBottom: "4px", fontWeight: 500}}>Select Financial Year </label>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={loaderStatus}
                style={{
                  backgroundColor: "#4f2d7f",
                  color: "white",
                  height: "30px",
                  paddingLeft: "2em",
                  paddingRight: "2em",
                  textTransform: "capitalize",
                  opacity: loaderStatus ? 0.6 : 1,
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", width: "100%" }}>
          <div style={{ width: "65%" }}>
            {/* Reserved space for additional controls */}
          </div>
          {/* Conditionally render buttons only when Save tab is active */}
          {activeTab === "1" && (
            <div style={{ display: "flex", width: "35%" }}>
              <Button
                onClick={handleSaveToGSTN}
                variant="contained"
                color="primary"
                disabled={loaderStatus || selectedSave?.length === 0}
                style={{
                  backgroundColor:
                    selectedSave?.length === 0 ? "#ccc" : "#4f2d7f",
                  color: "white",
                  height: "30px",
                  margin: "1.8em 5px 0px 0px",
                  paddingLeft: "2em",
                  paddingRight: "2em",
                  textTransform: "capitalize",
                  opacity: loaderStatus ? 0.6 : 1,
                }}
              >
                Save to GSTN
              </Button>
              <Button
                onClick={handleDownloadJSON}
                variant="contained"
                color="primary"
                disabled={loaderStatus || selectedSave?.length === 0}
                style={{
                  backgroundColor: selectedSave?.length === 0
                    ? "#ccc"
                    : "#4f2d7f",
                  color: "white",
                  height: "30px",
                  margin: "1.8em 5px 0px 0px",
                  paddingLeft: "2em",
                  paddingRight: "2em",
                  textTransform: "capitalize",
                  opacity: loaderStatus ? 0.6 : 1,
                }}
              >
                Download JSON
              </Button>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            height: "3.6em",
            boxShadow: "0px 2px 3px 0px #8080805e",
            paddingRight: "5px",
            marginTop: "1em",
          }}
        >
          <TableHeader
            handleTabChange={handleTabChange}
            columns={headerColumns}
            activeTab={activeTab}
          />
        </div>

        {/* Conditional rendering based on active tab */}
        {activeTab === "1" && (
          <div style={{ marginTop: "1em" }}>
            <SaveTab />
          </div>
        )}

        {activeTab === "2" && (
          <div style={{ marginTop: "1em" }}>
            <FetchedSummary
              selectedGstinList={selectedGstinList}
              selectedReturnPeriod={selectedReturnPeriod}
              validateRequiredFields={validateRequiredFields}
              getFetchData={getFetchData}
              clearAndRestartInterval={clearAndRestartInterval}
            />
          </div>
        )}
      </NavBar>
    </div>
  );
};

export default Save;
